import React from "react";

import * as FolderAPI from "api/FolderAPI";
import * as DatasetAPI from "api/DatasetAPI";
import * as ReportAPI from "api/ReportAPI";
import * as BlueprintAPI from "api/BlueprintAPI";

import * as accountsReducer from "containers/Accounts/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as viewReducer from "containers/View/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as reportReducer from "containers/Report/ReportReducer";
import * as adminReducer from "containers/Admin/reducer";

import { toast } from "react-toastify";

import { FOLDER_ROLES } from "containers/App/reducer";
import { getIsEDG } from "containers/Gallery/GalleryReducer";
import { isEnabled } from "@/utils/featureFlags";
import { fetchAllSandboxes } from "@/containers/OpenClaw/openClawSlice";

const RESET_FOLDERS = "folders/RESET_FOLDERS";
const UPDATE_FOLDERS = "folders/UPDATE_FOLDERS";
const UPDATE_FOLDER = "folders/UPDATE_FOLDER";
const UPDATE_PROPERTY = "folders/UPDATE_PROPERTY";
const DELETE_FOLDER = "folders/DELETE_FOLDER";
const UPDATE_FIELD = "folders/UPDATE_FIELD";

export const FOLDER_ROLES_MAPPING = {
  OWNER: "EDITOR",
  EDITOR: "EXPLORER",
  VIEWER: "COLLABORATOR"
};

const initialState = {
  selectedFolder: undefined,
  defaultFolder: undefined,
  moveReportId: undefined,
  folders: {}
};

const DEFAULT_FOLDER = {
  isLoading: false
  // accountName: ""
};

export const AIBLE_SAMPLE_FOLDER_NAME = "AIBLE SAMPLE DATA";

export default function foldersReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    case UPDATE_FOLDERS:
      return {
        ...state,
        folders: {
          ...state.folders,
          ...action.data
        }
      };
    case UPDATE_FOLDER:
      return {
        ...state,
        folders: {
          ...state.folders,
          [action.id]: action.data
        }
      };
    case DELETE_FOLDER:
      const newFolders = { ...state.folders };
      delete newFolders[action.id];
      return {
        ...state,
        folders: newFolders
      };
    case UPDATE_PROPERTY:
      return {
        ...state,
        folders: {
          ...state.folders,
          [action.id]: {
            ...state[folders][action.id],
            [action.key]: action.data
          }
        }
      };
    case RESET_FOLDERS:
      return { ...initialState };
    default:
      return state;
  }
}

export function parseRoleType(roleType) {
  const fRole = FOLDER_ROLES.find((r) => r.value === roleType);
  return fRole && fRole.label;
}

/* Selectors */
export function getDefaultFolderId(state) {
  const tenantId =
    state && loginReducer.getSelectedTenant(state)
      ? loginReducer.getSelectedTenant(state).id
      : loginReducer.getLatestTenantId(state);
  const retDefaultFolderId =
    state && getDefaultFolder(state) ? getDefaultFolder(state) : loginReducer.getDefaultFolder(state, tenantId);
  return isNaN(retDefaultFolderId) ? retDefaultFolderId : Number(retDefaultFolderId);
}

export function getLastFolderId(state) {
  const lastFolderId = loginReducer.getItem(state, "aible_last_folder_id");

  return lastFolderId ? parseInt(lastFolderId) : lastFolderId;
}

export function getGlobal(state) {
  return state.folders;
}

export function getFolders(state) {
  return getGlobal(state).folders;
}

export function getFoldersArr(state) {
  return Object.values(getFolders(state));
}

export function getFolder(state, id) {
  return getFolders(state)[id] || {};
}

export function getFolderUsers(state, id) {
  const users = adminReducer.getActiveUsers(state) ?? [];
  const roles = getRoles(state, id) ?? [];

  const rolesObj = {};
  roles.forEach((r) => {
    const roleId = r?.role_user?.id;
    rolesObj[roleId] = r?.role_type;
  });

  const folderUsers = users
    .filter((u) => {
      if (rolesObj[u?.id]) {
        return true;
      }
      return false;
    })
    .map((u) => ({
      label: u.fullname + " " + u.email,
      fullname: u.fullname,
      email: u.email,
      value: u.id,
      id: u.id,
      role: rolesObj[u.id]
    }));

  return folderUsers;
}

export function getName(state, id) {
  return getFolder(state, id).name;
}

export function getDescription(state, id) {
  return getFolder(state, id).description;
}

export function getIsLoading(state, id) {
  return getFolders(state).isLoading;
}

export function getRoles(state, id) {
  return getFolder(state, id).roles;
}

export function getUserRole(state, id) {
  const roles = getRoles(state, id);
  const userId = loginReducer.getUserID(state);
  return roles && roles.find((role) => role.role_user && role.role_user.id === parseInt(userId));
}

export function getUserRoleType(state, id) {
  const userRole = getUserRole(state, id);
  return userRole && userRole.role_type;
}

export function getContentCount(state, id) {
  return getFolder(state, id).contents;
}

export function getPermissions(state, id) {
  const folder = getFolder(state, id);
  return folder && folder.permissions;
}

export function getUserDeletePermission(state, id) {
  const permissions = getPermissions(state, id);
  return permissions && permissions.can_delete;
}

export function getUserEditPermission(state, id) {
  const permissions = getPermissions(state, id);
  return permissions && permissions.can_edit;
}

export function getUserManagePermissions(state, id) {
  const permissions = getPermissions(state, id);
  return permissions && permissions.can_manage_permissions;
}

export function getSelectedFolder(state) {
  return getGlobal(state).selectedFolder;
}

export function getDefaultFolder(state) {
  return getGlobal(state).defaultFolder;
}

export function getMoveBlueprintId(state) {
  return getGlobal(state).moveBlueprintId;
}

export function getMoveBlueprint(state) {
  const moveBlueprintId = getMoveBlueprintId(state);
  const moveBlueprint = blueprintReducer.getBlueprint(state, moveBlueprintId);

  return moveBlueprint;
}

export function getMoveReportId(state) {
  return getGlobal(state).moveReportId;
}

export function getMoveReport(state) {
  const moveReportId = getMoveReportId(state);
  const moveReport = reportReducer.getReport(state, moveReportId);

  return moveReport;
}

export function getSelectedFolderData(state, id) {
  const selectedId = getSelectedFolder(state);
  return getFolder(state, selectedId);
}

export function getRoleOptions(state) {
  return FOLDER_ROLES;
}

export function getAibleSampleDataFolderId(state) {
  const foldersArr = getFoldersArr(state);
  const sampleDataFolder = foldersArr?.find((folder) => folder?.name === AIBLE_SAMPLE_FOLDER_NAME);
  return sampleDataFolder?.id;
}

/* Actions */
export function setDefaultFolder(id) {
  return (dispatch, getState) => {
    const tenantId = (loginReducer.getSelectedTenant(getState()) || {}).id;
    dispatch(loginReducer.storeDefaultFolder(tenantId, id));
    return dispatch(updateDefaultFolder(id));
  };
}

export function removeFolder(id) {
  return { type: DELETE_FOLDER, id };
}

export function resetFolders() {
  return { type: RESET_FOLDERS };
}

export function updateFolders(data) {
  return { type: UPDATE_FOLDERS, data };
}

export function updateFolder(id, data) {
  return { type: UPDATE_FOLDER, id, data };
}

export function updateIsLoading(id, key, data) {
  return {
    type: UPDATE_PROPERTY,
    id: id,
    [key]: data
  };
}

export function updateSelectedFolder(id) {
  return (dispatch, getState) => {
    dispatch(loginReducer.storeItem("aible_last_folder_id", id));

    return dispatch({
      type: UPDATE_FIELD,
      key: "selectedFolder",
      data: id
    });
  };
}

export function updateDefaultFolder(id) {
  return {
    type: UPDATE_FIELD,
    key: "defaultFolder",
    data: id
  };
}

export function handleMoveReport(id) {
  return {
    type: UPDATE_FIELD,
    key: "moveReportId",
    data: id
  };
}

export function updateMoveBlueprintId(id) {
  return {
    type: UPDATE_FIELD,
    key: "moveBlueprintId",
    data: id
  };
}

/* Methods */
export function init() {
  return (dispatch, getState) => {
    dispatch(fetchFolders()).then(() => {
      const selectedFolderId = getSelectedFolder(getState());
      const selectedFolder = getFolder(getState(), selectedFolderId);
      if (!selectedFolderId || selectedFolder?.id === undefined) {
        const defaultFolderId = getDefaultFolderId(getState());

        const lastFolderId = getLastFolderId();

        if (lastFolderId && getFolders(getState())[lastFolderId]) {
          dispatch(updateSelectedFolder(lastFolderId));
        } else if (defaultFolderId && getFolders(getState())[defaultFolderId]) {
          dispatch(updateSelectedFolder(defaultFolderId));
        } else {
          const folders = getFoldersArr(getState());
          if (folders && folders.length) {
            dispatch(updateSelectedFolder(folders[0].id));
          }
        }
      }
      return dispatch(accountsReducer.fetchAll(true, true));
    });
  };
}

function transformFolder(state, folder) {
  const retFolder = {
    ...DEFAULT_FOLDER,
    ...folder
  };

  // const folderAccount = retFolder.account_id;

  // if (folderAccount) {
  //   retFolder.accountName = accountsReducer.getAccountName(state, folderAccount);
  // }

  return retFolder;
}

export function fetchFolders() {
  return (dispatch, getState) => {
    return FolderAPI.getFolders().then((folders) => {
      const retFolders = {};
      const state = getState();
      if (folders && folders.length) {
        folders.map((folder) => {
          retFolders[folder.id] = transformFolder(state, folder);
        });
      }
      return dispatch(updateFolders(retFolders));
    });
  };
}

export function fetchFolder(id) {
  return (dispatch, getState) => {
    return FolderAPI.getFolder(id).then((folder) => {
      return dispatch(updateFolder(folder.id, transformFolder(getState(), folder)));
    });
  };
}

export function selectFolder(id, isSetDefault = true) {
  return (dispatch, getState) => {
    // dispatch(loginReducer.handleStateReset());
    dispatch(viewReducer.updateSearchValue(""));
    dispatch(viewReducer.updateDatasetSearchResult(undefined));
    dispatch(reportReducer.updateBase({}));
    dispatch(updateSelectedFolder(id));
    if (isSetDefault === true) {
      dispatch(setDefaultFolder(id));
    }
    if (isEnabled("openshell")) {
      dispatch(fetchAllSandboxes());
    }
    return dispatch(accountsReducer.fetchAll(true));
  };
}

export function createFolder({ name, description, collaborators, defaultRoleType } = {}) {
  return (dispatch, getState) => {
    return FolderAPI.createFolder(name, description, collaborators, defaultRoleType).then((response) => {
      dispatch(updateFolder(response.id, transformFolder(getState(), response)));
      return dispatch(selectFolder(response.id));
    });
  };
}

export function moveDatasetFolder(datasetId, folderId) {
  return (dispatch, getState) => {
    console.log("move dataset folder", datasetId, folderId);
    const dataset = homeReducer.getDataset(getState(), datasetId);

    return DatasetAPI.moveDatasetFolder(datasetId, folderId).then((response) => {
      console.log("move dataset resp", response);

      toast(<div>{"Dataset " + dataset?.name + " has been moved to folder. " + folderId}</div>, {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });
    });
  };
}

export function moveReportFolder(reportId, folderId) {
  return (dispatch, getState) => {
    console.log("move report folder", reportId, folderId);
    const report = reportReducer.getReport(getState(), reportId);

    return ReportAPI.moveReportFolder(reportId, folderId).then((response) => {
      console.log("move report resp", response);

      toast(<div>{"Report " + report?.name + " has been moved to folder. " + folderId}</div>, {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });

      return dispatch(reportReducer.updateReportR(response?.id, response));
    });
  };
}

export function moveBlueprintFolder(blueprintId, folderId) {
  return (dispatch, getState) => {
    console.log("move blueprint folder", blueprintId, folderId);
    const blueprint = blueprintReducer.getBlueprint(getState(), blueprintId);

    return BlueprintAPI.moveBlueprintFolder(blueprintId, folderId).then((response) => {
      console.log("move blueprint resp", response);

      toast(<div>{"Blueprint " + blueprint?.name + " has been moved to folder. " + folderId}</div>, {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });

      return dispatch(blueprintReducer.updateBlueprint(response?.id, response));
    });
  };
}

export function patchFolder({ id, name, description } = {}) {
  return (dispatch, getState) => {
    const currFolder = getFolder(getState(), id);
    const retData = {};
    if (name && name !== currFolder.name) {
      retData.name = name;
    }
    if (description !== undefined && description !== currFolder.description) {
      retData.description = description;
    }
    return FolderAPI.patchFolder(id, retData).then((response) => {
      dispatch(updateFolder(response.id, transformFolder(getState(), response)));
      return;
    });
  };
}

export function deleteFolder(id) {
  return (dispatch, getState) => {
    const shouldDelete = confirm(
      "Deleting this folder will also delete the contents of this folder." +
        "This includes any projects, blueprints, datasets, and deployments associated with the folder." +
        "Are you sure you want to proceed?"
    );
    if (shouldDelete) {
      return FolderAPI.deleteFolder(id).then((response) => {
        dispatch(removeFolder(response.id));
        const selectedFolderId = getSelectedFolder(getState());
        const folders = getFoldersArr(getState());
        const newFolder = folders && folders[0];
        if (selectedFolderId === response.id) {
          dispatch(selectFolder(newFolder && newFolder.id));
        }
      });
    }
  };
}

export function addUserRole(id, user, role, params) {
  return (dispatch, getState) => {
    return FolderAPI.addUserRoleToFolder(id, user, role, params).then((r) => {
      return FolderAPI.getFolder(id).then((response) => {
        return dispatch(updateFolder(response.id, transformFolder(getState(), response)));
      });
    });
  };
}

export function deleteUserRole(id, folderRoleId) {
  return (dispatch, getState) => {
    return FolderAPI.deleteUserRoleFromFolder(folderRoleId).then((r) => {
      return FolderAPI.getFolder(id).then((response) => {
        return dispatch(updateFolder(response.id, transformFolder(getState(), response)));
      });
    });
  };
}

export function patchUserRole(id, folderRoleId, role) {
  return (dispatch, getState) => {
    return FolderAPI.patchUserRoleFolder(folderRoleId, role).then((r) => {
      return FolderAPI.getFolder(id).then((response) => {
        return dispatch(updateFolder(response.id, transformFolder(getState(), response)));
      });
    });
  };
}
